import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError, formatDuration } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { Reveal, Overline } from "./Reveal";
import { CheckCircle2, Loader2 } from "lucide-react";

const DAY_LABELS = [["0", "Lun"], ["1", "Mar"], ["2", "Mer"], ["3", "Jeu"], ["4", "Ven"], ["5", "Sam"], ["6", "Dim"]];

export default function Booking() {
  const [categories, setCategories] = useState([]);
  const [team, setTeam] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [date, setDate] = useState(undefined);
  const [slots, setSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ client_name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const { settings } = useSettings();
  const schedule = settings.schedule || {};

  useEffect(() => {
    api.get("/services").then((res) => setCategories(res.data.categories)).catch(() => {});
    api.get("/team").then((res) => setTeam(res.data.team)).catch(() => {});
  }, []);

  useEffect(() => {
    const onPreselect = (e) => {
      setServiceId(e.detail);
      setStaffId("");
      setTime("");
      setDate(undefined);
    };
    window.addEventListener("preselect-service", onPreselect);
    return () => window.removeEventListener("preselect-service", onPreselect);
  }, []);

  const selectedService = useMemo(
    () => categories.flatMap((c) => c.services).find((s) => s.id === serviceId),
    [categories, serviceId]
  );

  const depositDue = selectedService?.deposit_amount || 0;
  const remaining = selectedService ? Math.max(0, selectedService.price - depositDue) : 0;

  const eligibleStaff = useMemo(
    () => (selectedService ? team.filter((m) => m.specialties.includes(selectedService.category)) : []),
    [team, selectedService]
  );

  useEffect(() => {
    if (eligibleStaff.length === 1) setStaffId(eligibleStaff[0].id);
  }, [eligibleStaff]);

  useEffect(() => {
    if (!date || !serviceId || !staffId) return;
    setLoadingSlots(true);
    setTime("");
    api.get(`/availability?date=${format(date, "yyyy-MM-dd")}&service_id=${serviceId}&staff_id=${staffId}`)
      .then((res) => setSlots(res.data))
      .catch(() => toast.error("Impossible de charger les créneaux."))
      .finally(() => setLoadingSlots(false));
  }, [date, serviceId, staffId]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", {
        ...form,
        service_id: serviceId,
        staff_id: staffId,
        date: format(date, "yyyy-MM-dd"),
        time,
      });
      const booking = res.data.booking;
      if ((booking.deposit_amount || 0) > 0 && booking.payment_status === "unpaid") {
        toast.info("Redirection vers le paiement sécurisé de l'acompte…");
        const co = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin });
        window.location.href = co.data.checkout_url;
        return;
      }
      setConfirmed(booking);
      toast.success("Demande envoyée ! Vous recevrez un email de confirmation.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setConfirmed(null);
    setTime("");
    setForm({ client_name: "", email: "", phone: "", message: "" });
  };

  return (
    <section id="reservation" className="bg-[var(--brand-section)] py-28 lg:py-40" data-testid="booking-section">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-5 lg:px-12">
        <Reveal className="lg:col-span-2">
          <Overline className="mb-6">Réservation</Overline>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[var(--brand-dark)]">
            Réservez votre <span className="italic font-light text-[var(--brand-accent)]">moment.</span>
          </h2>
          <p className="mt-8 max-w-sm text-base font-light leading-relaxed text-[var(--brand-muted)]">
            Choisissez votre prestation, votre professionnelle, puis un créneau en temps réel. Vous recevez une confirmation par email — simple comme bonjour.
          </p>
          <div className="mt-10 border-l-2 border-[var(--brand-accent)]/40 pl-6 text-sm font-light text-[var(--brand-muted)]" data-testid="booking-hours">
            <p className="uppercase tracking-[0.2em] text-xs text-[var(--brand-accent)]">Horaires</p>
            {DAY_LABELS.map(([key, label]) => {
              const day = schedule[key];
              if (!day) return null;
              return (
                <p key={key} className={day.closed ? "opacity-50" : ""}>
                  {label} · {day.closed ? "fermé" : `${day.open} – ${day.close}`}
                </p>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-3">
          {confirmed ? (
            <div className="flex h-full flex-col items-start justify-center border border-black/5 bg-[var(--brand-bg)] p-10 lg:p-16" data-testid="booking-confirmation">
              <CheckCircle2 size={40} className="text-[var(--brand-accent)]" strokeWidth={1.5} />
              <h3 className="font-display mt-6 text-3xl text-[var(--brand-dark)]">Merci {confirmed.client_name} !</h3>
              <p className="mt-4 max-w-md text-base font-light leading-relaxed text-[var(--brand-muted)]">
                Votre demande de <strong className="font-medium">{confirmed.service_name}</strong>
                {confirmed.staff_name ? <> avec <strong className="font-medium">{confirmed.staff_name}</strong></> : null} le{" "}
                <strong className="font-medium">{format(new Date(confirmed.date + "T12:00:00"), "EEEE d MMMM yyyy", { locale: fr })}</strong> à{" "}
                <strong className="font-medium">{confirmed.time}</strong> a bien été envoyée. Un email récapitulatif arrive dans votre boîte.
              </p>
              <button onClick={reset}
                      className="mt-10 rounded-full border border-[var(--brand-dark)] px-8 py-4 text-xs uppercase tracking-[0.25em] text-[var(--brand-dark)] transition-colors duration-300 hover:bg-[var(--brand-dark)] hover:text-[var(--brand-bg)]"
                      data-testid="booking-new-btn">
                Réserver un autre soin
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-black/5 bg-[var(--brand-bg)] p-8 lg:p-12" data-testid="booking-form">
              <div className="grid gap-10">
                <div>
                  <label className="text-xs uppercase tracking-[0.25em] text-[var(--brand-accent)]">1 · La prestation</label>
                  <Select value={serviceId} onValueChange={(v) => { setServiceId(v); setStaffId(""); setTime(""); }}>
                    <SelectTrigger className="mt-4 h-14 rounded-none border-black/10 bg-transparent text-base font-light" data-testid="booking-service-select">
                      <SelectValue placeholder="Choisir une prestation" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectGroup key={cat.name}>
                          <SelectLabel className="text-xs uppercase tracking-[0.2em] text-[var(--brand-accent)]">{cat.name}</SelectLabel>
                          {cat.services.map((s) => (
                            <SelectItem key={s.id} value={s.id} data-testid={`booking-service-option-${s.id}`}>
                              {s.name} — {s.price} € · {formatDuration(s.duration)}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {serviceId && eligibleStaff.length > 0 && (
                  <div>
                    <label className="text-xs uppercase tracking-[0.25em] text-[var(--brand-accent)]">2 · La professionnelle</label>
                    <div className="mt-4 flex flex-wrap gap-4" data-testid="booking-staff-list">
                      {eligibleStaff.map((m) => (
                        <button type="button" key={m.id}
                                onClick={() => { setStaffId(m.id); setTime(""); }}
                                data-testid={`staff-btn-${m.id}`}
                                className={`flex items-center gap-3 rounded-full border py-2 pl-2 pr-6 text-sm font-light transition-colors duration-300 ${
                                  staffId === m.id
                                    ? "border-[var(--brand-dark)] bg-[var(--brand-dark)] text-[var(--brand-bg)]"
                                    : "border-black/15 text-[var(--brand-dark)] hover:border-[var(--brand-accent)]"
                                }`}>
                          <img src={m.photo} alt={m.name} className="h-10 w-10 rounded-full object-cover" />
                          <span>{m.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className={!staffId ? "pointer-events-none opacity-40" : ""}>
                  <label className="text-xs uppercase tracking-[0.25em] text-[var(--brand-accent)]">3 · La date</label>
                  <div className="mt-4 flex justify-center border border-black/5 bg-white/40 p-4" data-testid="booking-calendar">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      locale={fr}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || !!schedule[String(d.getDay())]?.closed}
                      className="font-light"
                    />
                  </div>
                </div>

                {date && serviceId && staffId && (
                  <div>
                    <label className="text-xs uppercase tracking-[0.25em] text-[var(--brand-accent)]">4 · L'heure</label>
                    {loadingSlots ? (
                      <div className="mt-6 flex items-center gap-3 text-sm font-light text-[var(--brand-muted)]" data-testid="booking-slots-loading">
                        <Loader2 size={16} className="animate-spin" /> Chargement des créneaux…
                      </div>
                    ) : slots?.closed ? (
                      <p className="mt-6 text-sm font-light italic text-[var(--brand-muted)]" data-testid="booking-closed-msg">
                        L'institut est fermé ce jour-là.
                      </p>
                    ) : slots && slots.slots.every((s) => !s.available) ? (
                      <p className="mt-6 text-sm font-light italic text-[var(--brand-muted)]" data-testid="booking-full-msg">
                        Complet ce jour-là — essayez une autre date.
                      </p>
                    ) : (
                      <div className="mt-6 grid grid-cols-4 gap-3 sm:grid-cols-5" data-testid="booking-slots">
                        {slots?.slots.map((s) => (
                          <button
                            type="button"
                            key={s.time}
                            disabled={!s.available}
                            onClick={() => setTime(s.time)}
                            data-testid={`slot-btn-${s.time.replace(":", "")}`}
                            className={`slot-pill border py-3 text-sm font-light ${
                              time === s.time
                                ? "border-[var(--brand-dark)] bg-[var(--brand-dark)] text-[var(--brand-bg)]"
                                : s.available
                                  ? "border-black/15 text-[var(--brand-dark)] hover:border-[var(--brand-accent)] hover:bg-[var(--brand-accent)] hover:text-[var(--brand-bg)]"
                                  : "cursor-not-allowed border-black/5 text-[var(--brand-dark)]/25 line-through"
                            }`}
                          >
                            {s.time}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {time && (
                  <div className="grid gap-5 border-t border-black/10 pt-8">
                    <label className="text-xs uppercase tracking-[0.25em] text-[var(--brand-accent)]">5 · Vos coordonnées</label>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Input required placeholder="Votre nom" value={form.client_name}
                             onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                             className="h-14 rounded-none border-black/10 bg-transparent font-light" data-testid="booking-name-input" />
                      <Input required type="tel" placeholder="Téléphone" value={form.phone}
                             onChange={(e) => setForm({ ...form, phone: e.target.value })}
                             className="h-14 rounded-none border-black/10 bg-transparent font-light" data-testid="booking-phone-input" />
                    </div>
                    <Input required type="email" placeholder="Email" value={form.email}
                           onChange={(e) => setForm({ ...form, email: e.target.value })}
                           className="h-14 rounded-none border-black/10 bg-transparent font-light" data-testid="booking-email-input" />
                    <Textarea placeholder="Un message ? (optionnel)" value={form.message}
                              onChange={(e) => setForm({ ...form, message: e.target.value })}
                              className="rounded-none border-black/10 bg-transparent font-light" data-testid="booking-message-input" />

                    {depositDue > 0 && (
                      <div className="border border-[var(--brand-accent)]/40 bg-[var(--brand-accent)]/10 p-6 text-sm font-light text-[var(--brand-dark)]" data-testid="booking-deposit-info">
                        <p className="flex justify-between"><span>Prix de la prestation</span><strong className="font-medium">{selectedService.price} €</strong></p>
                        <p className="mt-2 flex justify-between text-[var(--brand-accent)]"><span>Acompte à payer maintenant</span><strong className="font-medium">{depositDue} €</strong></p>
                        <p className="mt-2 flex justify-between"><span>Reste à régler à l'institut</span><strong className="font-medium">{remaining} €</strong></p>
                      </div>
                    )}

                    <div className="flex flex-wrap items-center justify-between gap-6 pt-2">
                      <p className="text-sm font-light text-[var(--brand-muted)]" data-testid="booking-summary">
                        {selectedService?.name} · {format(date, "d MMMM", { locale: fr })} à {time} · {selectedService?.price} €
                      </p>
                      <button type="submit" disabled={submitting}
                              className="rounded-full bg-[var(--brand-dark)] px-10 py-4 text-xs uppercase tracking-[0.25em] text-[var(--brand-bg)] transition-transform duration-300 hover:scale-[0.96] disabled:opacity-50"
                              data-testid="booking-submit-btn">
                        {submitting ? "Envoi…" : depositDue > 0 ? `Payer l'acompte · ${depositDue} €` : "Confirmer le rendez-vous"}
                      </button>
                    </div>
                    {depositDue === 0 && (
                      <p className="text-xs font-light italic text-[var(--brand-muted)]" data-testid="booking-deposit-note">
                        Le règlement se fait directement à l'institut.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
